/**
 * @class
 */
import Base from '@netx/core/lib/netx-model';

export default class Participant extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {string} */
	get idAttribute() {
		return 'uniqueId';
	}
	/** @type {string} */
	get titleAttribute() {
		return 'label';
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object} defaults               - Model defaults
	 * @property {string} defaults.uniqueId      - Participant `<type>-<id>`
	 * @property {number} defaults.id            - Participant id
	 * @property {string} defaults.label         - Participant label
	 * @property {string} defaults.permission    - Participant permission
	 * @property {number} defaults.resourceId    - Participant resource id
	 * @property {string} defaults.resourceName  - Participant resource name
	 * @property {string} defaults.type          - Participant type (all, group, user)
	 */
	get defaults() {
		return {
			id: 0,
			label: '',
			permission: 'read',
			resourceId: 0,
			resourceName: '',
			type: 'user',

			// We have an issue - the unique id for these records are two keys: id and type
			// Backbone does not support this, so we have to make our own single id on the client of the two
			// uniqueId     : ''
		};
	}

	/** @type {array} */
	get clientAttrs() {
		return ['uniqueId'];
	}

	/**
	 * @property {object} methods         - Model methods
	 * @property {method} methods.create  - Add particpant to shared resource
	 * @property {method} methods.remove  - Remove participant from shared resource
	 * @property {method} methods.update  - Update participant on shared resource
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			create: function (opts) {
				return ['addParticipant', this.self()];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			remove: function (opts) {
				return ['removeParticipant', this.self()];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			update: function (opts) {
				return ['updateParticipant', this.self()];
			},
		};
	}

	/**
	 * @property {object} parsers         - Model parsers
	 * @property {method} parsers.create  - Parse newly created participant
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {object} participant Participant
			 * @param {object} opts Request options
			 */
			create: function (participant, opts) {
				// We want to combine the type and id `<type>-<id>`
				// `all` has no id and is just "all"
				var uniqueId = _.compact(
					_.values(_.pick(participant, 'type', 'id')),
				).join('-');

				return _.extend(participant, {
					uniqueId: uniqueId,
				});
			},
		};
	}

	/**
	 * @override
	 * @param {string} attr Attribute
	 * @return {mixed} Value
	 */
	get(attr) {
		if (attr === 'label' && this.attributes.type === 'all') {
			return i18n.t('i18n.allUsers');
		}
		return super.get(attr);
	}

	/** @override */
	getLabel() {
		return this.get('type') === 'all'
			? i18n.t('i18n.allUsers')
			: super.getLabel();
	}

	/**
	 * @method
	 * @param {object} data Data
	 * @param {object} opts Request options
	 */
	parseAndSave(data, opts) {
		return this.save(
			_.pick.apply(_, [data].concat(_.keys(this.constructor.GetDefaults()))),
			opts,
		);
	}

	/** @type {string} */
	static get __name__() {
		return 'Participant';
	}
}
